import React, { useState } from 'react';
import type { Editor } from 'ckeditor5';
import { observer } from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as mobx from 'mobx';
import { CommonModal } from '../../../../../../../../global/modal/common/common-modal';
import { FormField } from '../../../../../../../../global/form/form-field/form-field';
import { FormFieldInput } from '../../../../../../../../global/form/form-field-input/form-field-input';
import { FormFieldError } from '../../../../../../../../global/form/form-field-input/form-field-error';
import { RichTextEditor } from '../../../../../../../../global/rich-text-editor';
import DefaultButton from '../../../../../../../../global/default-button/default-button';
import { OrganizationSelectInput } from './_add-speaker-organization/_speaker-organization-select/organization-select-trigger';
import { SpeakerOrganizationForm } from './_add-speaker-organization/speaker-organization-form';
import { UploadImageS3Presentation } from '../../../../../../../../_events/_manage-event/shared/upload-image/upload-image-to-s3.presentation';
import { ADD_TEXT, APPLY_TEXT, CANCEL_TEXT } from '../../../../../../../../../constants/common-labels.constants';
import { DIMENSIONS_PROFILE_IMAGE } from '../../../../../../../../../constants/file.constants';
import {
    ADD_SPEAKER_MODAL_TITLE_TEXT,
    EDIT_SPEAKER_MODAL_TITLE_TEXT,
    NEW_ORGANIZATION_BUTTON_TEXT,
    NO_ACCOUNT_ORGANIZATIONS_TEXT,
    NO_MATCHING_ACCOUNT_ORGANIZATIONS_SEARCH_TEXT,
    SPEAKER_BIO_TEXT,
    SPEAKER_FIRST_NAME_TEXT,
    SPEAKER_LAST_NAME_TEXT,
    SPEAKER_ORGANIZATION_TEXT,
    SPEAKER_PROFILE_IMAGE_TEXT,
    SPEAKER_TITLE_TEXT,
} from '../../../../../../../event-hubs.text';
import { SpeakerFormVM } from './speaker-form.viewmodel';
import { ACCOUNT_ORGANIZATION_ID, BIOGRAPHY, FIRST_NAME, LAST_NAME, TITLE } from './speaker-form.config';
import './speaker-form-modal.scss';

type SpeakerFormPresentationProps = {
    vm: SpeakerFormVM;
};

const SpeakerFormPresentation: React.FC<SpeakerFormPresentationProps> = observer(({ vm }) => {
    const { formatMessage } = useIntl();
    const [speakerModalElement, setSpeakerModalElement] = useState<HTMLDivElement | null>(null);

    const firstNameField = vm.getFormVmValuePropsByKey(FIRST_NAME);
    const lastNameField = vm.getFormVmValuePropsByKey(LAST_NAME);
    const titleField = vm.getFormVmValuePropsByKey(TITLE);
    const organizationField = vm.getFormVmValuePropsByKey(ACCOUNT_ORGANIZATION_ID);
    const bioField = vm.getFormVmValuePropsByKey(BIOGRAPHY);

    const handleBioChange = (_: unknown, editor: Editor) => bioField.setValue(editor.getData());

    return (
        <>
        <CommonModal
            isOpen
            title={<FormattedMessage {...(vm.isEditMode() ? EDIT_SPEAKER_MODAL_TITLE_TEXT : ADD_SPEAKER_MODAL_TITLE_TEXT)} />}
            onRequestClose={vm.isSaving ? undefined : vm.cancel}
            className='speaker-form-modal'
            contentRef={setSpeakerModalElement}
            footer={
                <div
                    className='speaker-form-modal__footer'
                    onMouseDown={event => event.preventDefault()}
                >
                    <DefaultButton
                        modifier='cancel'
                        text={<FormattedMessage {...CANCEL_TEXT} />}
                        onClick={vm.cancel}
                        disabled={vm.isSaving}
                    />
                    <DefaultButton
                        text={<FormattedMessage {...(vm.isEditMode() ? APPLY_TEXT : ADD_TEXT)} />}
                        onClick={vm.submit}
                        disabled={vm.isSaving || vm.isImageUploading()}
                    />
                </div>
            }
        >
            <div className='speaker-form-modal__body'>
                <div className='speaker-form-modal__row'>
                    <FormFieldInput.Text
                        field={firstNameField}
                        labelDescriptor={SPEAKER_FIRST_NAME_TEXT}
                        additionalClassName='speaker-form-modal__field'
                        withError
                    />
                    <FormFieldInput.Text
                        field={lastNameField}
                        labelDescriptor={SPEAKER_LAST_NAME_TEXT}
                        additionalClassName='speaker-form-modal__field'
                        withError
                    />
                </div>
                <div className='speaker-form-modal__row'>
                    <FormFieldInput.Text
                        field={titleField}
                        labelDescriptor={SPEAKER_TITLE_TEXT}
                        additionalClassName='speaker-form-modal__field'
                        withError
                    />
                    <FormField
                        className='speaker-form-modal__field'
                        label={<FormattedMessage {...SPEAKER_ORGANIZATION_TEXT} />}
                    >
                        <OrganizationSelectInput
                            keyProperty='accountOrganizationId'
                            nameProperty='name'
                            position='bottom'
                            align='start'
                            containerClassName='speaker-organization-select-popover'
                            value={organizationField.value as number}
                            items={vm.getOrganizations()}
                            onChange={mobx.action((newValue: number) => {
                                organizationField.setValue(newValue);
                                organizationField.setTouched();
                            })}
                            ActionButton={() => (
                                <DefaultButton
                                    className='add-line-to-table-btn'
                                    text={<FormattedMessage {...NEW_ORGANIZATION_BUTTON_TEXT} values={{ prefix: '+ ' }} />}
                                    onClick={vm.openAddOrganizationModal}
                                />
                            )}
                            emptyListText={<FormattedMessage {...NO_ACCOUNT_ORGANIZATIONS_TEXT} />}
                            emptyFilterText={<FormattedMessage {...NO_MATCHING_ACCOUNT_ORGANIZATIONS_SEARCH_TEXT} />}
                        />
                        {organizationField.touched && !organizationField.valid &&
                            <span className='oe-form-field__error'>
                                <FormattedMessage
                                    values={{ fieldName: formatMessage(SPEAKER_ORGANIZATION_TEXT) }}
                                    {...organizationField.validationMessage}
                                />
                            </span>}
                    </FormField>
                </div>
                <UploadImageS3Presentation
                    vm={vm.getUploadProfileImageS3VM()}
                    minDimensions={DIMENSIONS_PROFILE_IMAGE}
                    fieldName={formatMessage(SPEAKER_PROFILE_IMAGE_TEXT)}
                />
                <FormField label={<FormattedMessage {...SPEAKER_BIO_TEXT} />}>
                    <RichTextEditor
                        className='oe-form-field__control m-0px p-0px'
                        value={bioField.value as string || ''}
                        onChange={handleBioChange}
                        onBlur={bioField.setTouched}
                    />
                    <FormFieldError field={bioField} labelDescriptor={SPEAKER_BIO_TEXT} />
                </FormField>
            </div>
        </CommonModal>

        {vm.isAddOrganizationModalOpen &&
            <SpeakerOrganizationForm
                accountId={vm.getAccountId()}
                existingOrganizationNames={vm.getOrganizationNames()}
                anchorElement={speakerModalElement}
                onOrganizationCreated={vm.applyNewOrganization}
                onCancel={vm.closeAddOrganizationModal}
            />}
        </>
    );
});

export { SpeakerFormPresentation };